import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Save, Link2, ArrowLeft, Copy, Sparkles, AlertTriangle, Mail, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHeader } from "@/components/AdminLayout";
import { HotelPicker } from "@/components/HotelPicker";
import { ChargeForm } from "@/components/ChargeForm";
import { PreviewDialog } from "@/components/PreviewDialog";
import { api, apiError } from "@/lib/api";
import { EMPTY_FORM, validateCharge, toPayload, chargeToForm, formToTemplate } from "@/lib/validate";
import { labelFlights } from "@/lib/flights";
import { DEMO_PREVIEW, payLink, chargeLink, whatsappUrl, copyText, fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const LinkResult = ({ charge, onClose, hotel }) => (
  <Dialog open={!!charge} onOpenChange={(o) => { if (!o) onClose(); }}>
    <DialogContent data-testid="link-result-dialog">
      <DialogHeader>
        <DialogTitle>Enlace de pago generado</DialogTitle>
        <DialogDescription>Caduca el {charge && fmtDateTime(charge.expires_at)}.</DialogDescription>
      </DialogHeader>
      {charge?.email_result?.sent && <p className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="auto-email-sent">Enlace enviado automáticamente a {charge.email}.</p>}
      {charge?.email_result && !charge.email_result.sent && <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900" data-testid="auto-email-failed">No se pudo enviar el correo automático: {charge.email_result.error}. Puedes copiar el enlace o reintentar el envío.</p>}
      {charge && <p className="break-all rounded-lg bg-slate-50 p-3 font-mono text-sm" data-testid="generated-link">{chargeLink(charge)}</p>}
      <Button data-testid="copy-generated-link-btn" onClick={async () => { if (await copyText(chargeLink(charge))) toast.success("Enlace copiado"); }}><Copy size={16} className="mr-2" />Copiar enlace</Button>
      {charge && <Button asChild variant="outline" className="text-emerald-700" data-testid="generated-whatsapp-btn"><a href={whatsappUrl(charge, hotel)} target="_blank" rel="noreferrer"><MessageCircle size={16} className="mr-2" />Enviar por WhatsApp</a></Button>}
      {charge?.email && !charge.email_result?.sent && (
        <Button variant="outline" data-testid="send-generated-link-email-btn" onClick={async () => {
          try { await api.post(`/charges/${charge.id}/send-email`); toast.success(`Enlace enviado a ${charge.email}`); }
          catch (e) { toast.error(apiError(e).message); }
        }}><Mail size={16} className="mr-2" />Enviar por correo al huésped</Button>
      )}
    </DialogContent>
  </Dialog>
);

export default function NewCharge() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [hotels, setHotels] = useState([]);
  const [processors, setProcessors] = useState([]);
  const [hotelSlug, setHotelSlug] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [draftId, setDraftId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [linked, setLinked] = useState(null);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    api.get("/hotels").then((r) => setHotels(r.data));
    api.get("/processors").then((r) => setProcessors(r.data));
  }, []);
  useEffect(() => {
    if (["manager", "cashier", "agent"].includes(user?.role)) api.get("/my-groups").then((r) => setGroups(r.data));
  }, [user?.role]);

  useEffect(() => {
    const id = params.get("borrador");
    if (id) {
      api.get(`/charges/${id}`).then(({ data }) => {
        if (data.link_status !== "borrador") { toast.error("Este cobro ya no es un borrador"); return; }
        setDraftId(data.id); setHotelSlug(data.hotel_slug); setForm(chargeToForm(data));
      }).catch((e) => toast.error(apiError(e).message));
    }
  }, [params]);

  const hotel = hotels.find((h) => h.slug === hotelSlug);
  const selectedProc = processors.find((p) => p.id === form.processor_id);
  const anyConnected = useMemo(() => processors.some((p) => p.enabled && p.connected && p.currency === form.currency), [processors, form.currency]);
  const canGenerate = form.processor_id === "manual" || (selectedProc ? selectedProc.enabled && selectedProc.connected : anyConnected);

  const reset = () => { setHotelSlug(null); setForm(EMPTY_FORM); setErrors({}); setDraftId(null); setParams({}); };

  const saveDraft = async () => {
    const errs = validateCharge(form, false);
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error("Revisa los campos marcados"); return null; }
    const payload = toPayload(form, hotelSlug);
    const { data } = draftId ? await api.put(`/charges/${draftId}`, payload) : await api.post("/charges", payload);
    setDraftId(data.id);
    return data;
  };

  const onSave = async () => {
    setBusy(true);
    try { if (await saveDraft()) toast.success("Borrador guardado"); }
    catch (e) { const er = apiError(e); setErrors(er.fields); toast.error(er.message); }
    finally { setBusy(false); }
  };

  const onGenerate = async () => {
    const errs = validateCharge(form, true);
    if (hotel.properties?.length && !form.property_name) errs.property_name = "Selecciona el hotel";
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error("Completa los datos obligatorios"); return; }
    setBusy(true);
    try {
      const draft = await saveDraft();
      const { data } = await api.post(`/charges/${draft.id}/generate-link`);
      setLinked(data);
      reset();
    } catch (e) { const er = apiError(e); setErrors(er.fields); toast.error(er.message); }
    finally { setBusy(false); }
  };

  const demoHotel = hotels.find((h) => h.slug === "hard-rock");
  const demoBtn = <Button variant="outline" data-testid="btn-demo-hard-rock" disabled={!demoHotel} onClick={() => setPreview({ hotel: demoHotel, data: DEMO_PREVIEW })}><Sparkles size={16} className="mr-2" />Demo Hard Rock</Button>;

  if (!hotel) {
    return (
      <>
        <PageHeader title="Nuevo cobro" subtitle="Paso 1 de 2 · Selecciona la plantilla de la reservación." actions={demoBtn} />
        <HotelPicker hotels={hotels} value={hotelSlug} onChange={setHotelSlug} />
        <PreviewDialog open={!!preview} onOpenChange={() => setPreview(null)} hotel={preview?.hotel} data={preview?.data} />
        <LinkResult charge={linked} hotel={hotels.find((h) => h.slug === linked?.hotel_slug)} onClose={() => setLinked(null)} />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Nuevo cobro" subtitle={`Paso 2 de 2 · Datos de la reservación para ${hotel.display_name}${draftId ? " · Borrador guardado" : ""}`}
        actions={<Button variant="ghost" data-testid="btn-change-hotel" onClick={reset}><ArrowLeft size={16} className="mr-2" />Cambiar plantilla</Button>} />
      <div className="mb-6 flex items-center gap-4 rounded-2xl p-5 text-white" style={{ background: `linear-gradient(120deg, ${hotel.primary_color}, ${hotel.secondary_color})` }} data-testid="selected-hotel-banner">
        <span className="font-heading text-xl font-semibold">{hotel.display_name}</span>
      </div>
      <ChargeForm form={form} setForm={setForm} errors={errors} processors={processors} defaultLang={hotel.language || "es"} allowManual={!!user?.can_manual_link} groups={groups} properties={hotel.properties || []} propertyRooms={hotel.property_rooms || {}} />
      {!canGenerate && (
        <div className="mt-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="processor-pending-notice">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div><p className="font-semibold">Procesador pendiente de conexión</p>
            <p>Puedes guardar el borrador y revisar la plantilla. La generación de enlaces reales se habilitará cuando haya un procesador conectado en el servidor. <Link to="/panel/procesadores" className="underline">Ver procesadores</Link></p></div>
        </div>
      )}
      <div className="sticky bottom-0 z-10 -mx-4 mt-6 flex flex-wrap justify-end gap-3 border-t border-slate-200 bg-[#F5F6F8]/90 px-4 py-4 backdrop-blur sm:mx-0 sm:rounded-2xl sm:border">
        <Button variant="outline" data-testid="btn-preview-payment-link" onClick={async () => { const data = formToTemplate(form, selectedProc?.name); setPreview({ hotel: { ...hotel, language: form.language || hotel.language }, data: { ...data, flights: await labelFlights(data.flights) } }); }}><Eye size={16} className="mr-2" />Vista previa</Button>
        <Button variant="outline" data-testid="btn-save-draft" disabled={busy} onClick={onSave}><Save size={16} className="mr-2" />Guardar borrador</Button>
        <Button data-testid="btn-generate-payment-link" disabled={busy || !canGenerate} onClick={onGenerate} className="bg-[#2F5BFF] hover:bg-[#1F45E0]"><Link2 size={16} className="mr-2" />Generar enlace de pago</Button>
      </div>
      <PreviewDialog open={!!preview} onOpenChange={() => setPreview(null)} hotel={preview?.hotel} data={preview?.data} />
      <LinkResult charge={linked} hotel={hotels.find((h) => h.slug === linked?.hotel_slug)} onClose={() => setLinked(null)} />
    </>
  );
}
